const { forOwn, has, isPlainObject, transform, isEqual, pick, omit, set, get } = require('lodash');
const { layers: layerTypes, mediaTypes } = require('../constants/layers');
const { variantPropertyPaths } = require('../constants/variants');

/*
    getDeepDiff(base,other,exclude)
    base (Object)- Base object
    other (Object)- Compare to object
    exclude (Array)- Properties that needs to be excluded from diff, values of these properties will be untouched.
    propertiesToRemove (Array)- Properties that needs to be removed from the result.
    Example
    INPUT:
    var obj1 = {
        isDirty:true,
        data:{text:'Hello',color:'blue'},
        deepObj:{obj:{name:"abc"}},
        obj1Function:function(){return 'I am function 1'}
    }

    var obj2 = {
        isDirty:true,
        data:{text:'Hi',color:'blue'},
        deepObj:{obj:{name:"abc"}},
        obj1Function:function(){return 'I am function 1'}
    }
    getDeepDiff(obj1,obj2)

    OUTPUT:
        {
            data:{text:'Hi'},
            obj1Function:function(){return 'I am function 1'}
        }

*/
const getDeepDiff = (base, object, excludeFromComp, propertiesToRemove) => {
  if (!object) throw new Error(`The object to be compared with should be an object: ${object}`);
  if (!base) return object;
  const result = transform(object, (result, value, key) => {
    if((propertiesToRemove && propertiesToRemove.length >= 0 && propertiesToRemove.includes(key))){
      return;
    }
    if (!has(base, key)) result[key] = value;

    //Add the excluded object directly
    if(excludeFromComp && excludeFromComp.length >= 0 && excludeFromComp.includes(key) || typeof(value) == 'function'){
      result[key] = value;
    } else if (!isEqual(value, base[key])) {
      //check if it is object else add directly
      result[key] = isPlainObject(value) && isPlainObject(base[key]) ? getDeepDiff(base[key], value, excludeFromComp, propertiesToRemove) : value;
    }
  });

  //remove undefined objects
  forOwn(base, (value, key) => {
    if (!has(object, key)) delete result[key];
  });
  return result;
}

/*
    Gets list of all the keys in the object
*/

const getKeys = (a) => {
  let res = [];
  for (let key of Object.keys(a)) {
    res.push(key);
    if (typeof a[key] == 'object' && a[key] != null) {
      res.push(...getKeys(a[key]))
    }
  }
  return res;
}

/*
  Fetches the properties in 1 object based on the structure of second object. Stores the final result in a referenced param
*/
const recursiveExtract = (source, target, result) => {
  for (const key in target) {
    if (typeof target[key] === 'object' && !Array.isArray(target[key])) {
      // Recursively handle nested structures
      if (source[key] && typeof source[key] === 'object') {
        result[key] = {};
        recursiveExtract(source[key], target[key], result[key]);
      }
    } else {
      // Leaf property, fetch its value from the source object
      if (Array.isArray(source[key])) {
        result[key] = JSON.parse(JSON.stringify(source[key]));
      } else {
        result[key] = source[key];
      }
    }
  }
};

/*
    Object.assign's alternative for deep assign

    deepAssign(base,object)
    base - assign To Object
    object - assign From Object

    Example
    INPUT:
    var obj1 = {
        isDirty:true,
        data:{text:'Hello',color:'blue'},
        deepObj:{obj:{name:"abc"}},
        obj1Function:function(){return 'I am function 1'}
    }

    var obj2 = {
        isDirty:true,
        data:{text:'Hi',color:'red'},
        deepObj:{obj:{name:"abc"}},
        obj1Function:function(){return 'I am function 2'},
        isDirty:true
    }
    deepAssign(obj2,obj1)

    OUTPUT:
        {
          isDirty:true,
          data:{text:'Hello',color:'blue'},
          deepObj:{obj:{name:"abc"}},
          obj1Function:function(){return 'I am function 1'}
       }

*/

const isObject = (item)=> {
  return (item && typeof item === 'object' && !Array.isArray(item));
}
const deepAssign = (target, ...sources)=> {
  if (!sources.length) return target;
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }else{
          target[key] = Object.assign({}, target[key])
        }
        deepAssign(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepAssign(target, ...sources);
}

// Speaker image,subtitle need to be at top. Hence, other layers will be added at index below them.
const addLayerToLayersObject = (type, layers, insertIndex, data) => {
  var insertAtIndex = insertIndex ? insertIndex : layers.filter(x => [layerTypes.subtitle].includes(x.type)).length;
  layers.splice(insertAtIndex, 0, data);
  return insertAtIndex;
};

const getLoaderTextObjectData = (isHidden, type) => {
  const textLoaderData = {
    visible: !isHidden,
    evented: false,
    selectable: false,
    objectCaching: false,
    textBackgroundColor: '#FFFFFF',
    fill: '#000000',
    fontFamily: 'Roboto',
    fontSize: 24,
    textAlign: 'center',
    width: 300
  }
  if (type) {
    textLoaderData[`is${type}Loader`] = true;
  } else {
    textLoaderData.isLoader = true;
  }
  return textLoaderData;
}

const getComputedLayer = (layers, ad, layerId) => {
  const layer = layers.find(l => l.id === layerId);
  let computedLayer = JSON.parse(JSON.stringify(layer));
  if (ad.localLayers && ad.localLayers.length > 0) {
    const localLayer = ad.localLayers.find(l => l.id === layerId);
    deepAssign(computedLayer, localLayer);
  }
  return computedLayer;
};

function replaceWithCDN(srcUrl) {
  const s3Url = `${process.env.VUE_APP_STORAGE_URL}/AllFiles/Uploads/`;
  const cdnUrl = process.env.VUE_APP_CB_GALLERY_URL;

  if (srcUrl && srcUrl.startsWith(s3Url) && cdnUrl) {
    const replacedUrl = srcUrl.replace(s3Url, cdnUrl);
    return replacedUrl;
  }

  return srcUrl;
};

const replaceMediaUrlWithCdn = (media, mediaType) => {
  if (mediaType === mediaTypes.image || mediaType === mediaTypes.aiGeneratedImage) {
    media[mediaType] && media[`${mediaType}s`].forEach(image => {
      image.imageUrl = replaceWithCDN(image.imageUrl);
    });
  } else if (mediaType === mediaTypes.video) {
    media.videos && media.videos.forEach(video => {
      video.url = replaceWithCDN(video.url);
    });
  } else {
    media.audios && media.audios.forEach(audio => {
      audio.url = replaceWithCDN(audio.url);
    });
  }
};

const getInsertAtIndex = function(layers, canvasLayers, insertAtLayerId, insertingLayerId) {
  let adLayerIndex = 0;
  for(let i = layers.length - 1; i >= 0; i--) {
    let insertingLayerIndex = layers.findIndex(layer => layer.id === insertingLayerId);
    let insertAtLayerIndex = layers.findIndex(layer => layer.id === insertAtLayerId);
    // if dropping the layer down from the current position
    if(insertAtLayerIndex > insertingLayerIndex) {
      if(layers[i].id === insertAtLayerId) break;
    }
    let fabricObject = canvasLayers.findIndex(obj => obj.layerId === layers[i].id);
    // As we will be reordering the canvas layers we will only count those
    if(fabricObject !== -1) {
      if(layers[i].type !== layerTypes.background && layers[i].type !== layerTypes.border && !(layers[i].id === insertingLayerId)) {
        adLayerIndex++;
      }
    }
    if(layers[i].id === insertAtLayerId) break;
  }
  return adLayerIndex;
}

// This function returns the filtered data with supported properties and unsupported properties
const filterProperties = (layer, supportedFocusModeProperties, commonProperties) => {
  if(!layer || !supportedFocusModeProperties) return;
  // Filter the data with supported properties and unsupported properties
  const filteredDataWithSupportedProperties = pick(layer, supportedFocusModeProperties);
  const filteredDataWithoutSupportedProperties = omit(layer, supportedFocusModeProperties);
  // Add the common properties to the filtered data with supported properties
  if(commonProperties) {
    for(let key of commonProperties) {
      const originalValue = get(layer, key);
      if(originalValue) {
        set(filteredDataWithSupportedProperties, key, originalValue);
      }
    }
  }

  return {
    filteredDataWithSupportedProperties,
    filteredDataWithoutSupportedProperties
  };
}

const roundOffPropertiesInObject = (object, precision) => {
  const multiplicationFactor = 10 ** precision;
  forOwn(object, (value, key) => {
    if (!isNaN(value)) {
      object[key] = Math.round((value + Number.EPSILON) * multiplicationFactor) / multiplicationFactor;
    }
  });
};

const getAllPathsInObject = (object, currentPath = '') => {
  let paths = [];

  for (let key in object) {
    let newPath = currentPath ? `${currentPath}.${key}` : key;
    if (typeof object[key] === 'object' && object[key] !== null && !Array.isArray(object[key])) {
      paths = paths.concat(getAllPathsInObject(object[key], newPath));
    } else {
      paths.push(newPath);
    }
  }

  return paths;
};

const flattenObject = (obj, prefix = '') => {
  let result = {};

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) {
      Object.assign(result, flattenObject(value, newKey));
    } else {
      result[newKey] = value;
    }
  }

  return result;
}

const sanitizeNumber = (str, currentValue) => {
  if(currentValue) {
    return str !== currentValue ? str.replace(/[^\d.-]/g, '') : '0';
  }
  return str.replace(/[^\d.-]/g, '');
}

const getIsImageRegenerationRequired = (variants, selectedVariant) => {
  return variants.some(variant => {
    if(variant.id === selectedVariant.id) return false;
    return variant.layers.some(l => {
      if([layerTypes.image, layerTypes.background].includes(l.type)){
        return l.config.some(c => c.path == variantPropertyPaths.aiImageGenerationApiCallRequired && c.value == true);
      }
      return false;
    })
  });
}

function isUrl(str) {
  try {
    new URL(str);
    return true;
  } catch (e) {
    return false;
  }
}

// Function to proxy external URLs to avoid CORS issues
const getProxiedUrl = (originalUrl, shouldProxy = false) => {
  if (!shouldProxy || !originalUrl) {
    return originalUrl;
  }
  
  // Check if URL is already proxied to prevent double proxying
  if (originalUrl.includes('proxy.stunnr.co/proxy?url=')) {
    return originalUrl;
  }
  
  // If it's an HTTP/HTTPS URL, proxy it
  if (originalUrl.startsWith('http')) {
    return `https://proxy.stunnr.co/proxy?url=${encodeURIComponent(originalUrl)}`;
  }
  
  return originalUrl;
};

// Function to extract image name from URL, handling both regular and proxied URLs
const getImageNameFromUrl = (imageUrl) => {
  if (!imageUrl) {
    return '';
  }
  
  // If it's a proxied URL, extract the original URL first
  if (imageUrl.includes('proxy.stunnr.co/proxy?url=')) {
    try {
      const encodedUrl = imageUrl.split('proxy?url=')[1];
      const originalUrl = decodeURIComponent(encodedUrl);
      return originalUrl.split('/').pop();
    } catch (error) {
      // If decoding fails, fall back to the original method
      return imageUrl.split('/').pop();
    }
  }
  
  // For regular URLs, just get the filename
  return imageUrl.split('/').pop();
};

function hasNestedKey(obj, path) {
  return path.split('.').every(key => obj && (obj = obj[key]) !== undefined);
}

const extractFileName = (input, includeExtension = false) => {
  const fallback = `shape_${Date.now()}`;

  if (!input || typeof input !== "string") {
    return fallback;
  }

  try {
    // Attempt to extract the last valid URL from malformed strings
    const urlMatch = input.match(/https?:\/\/[^\s]+/g);
    const urlString = urlMatch ? urlMatch[urlMatch.length - 1] : input;

    const url = new URL(urlString);

    // Get filename from pathname
    let filename = url.pathname.split("/").pop();

    if (!filename) {
      return fallback;
    }

    // Decode URL-encoded names
    filename = decodeURIComponent(filename);

    // Remove extension only if not requested
    if (!includeExtension) {
      filename = filename.replace(/\.[^/.]+$/, "");
    }

    // Validate extracted name
    if (!filename || filename.length < 2) {
      return fallback;
    }

    return filename;
  } catch {
    return fallback;
  }
};

const extractCleanFontFamily = (fontValue) => {
  if (!fontValue) return fontValue;

  if (/\bregular\b/i.test(fontValue)) {
    fontValue = fontValue.replace(/\bregular\b/i, '').replace(/\s+/g, ' ').trim();
  } else {
    ['italic', 'bold', 'demo'].forEach(keyword => {
      fontValue = fontValue.replace(new RegExp(`\\b${keyword}\\b`, 'i'), '');
    });
    fontValue = fontValue.replace(/\s+/g, ' ').trim();
  }

  const parts = fontValue.split(' ');
  if (parts.length === 2 && parts[0] === parts[1]) {
    fontValue = parts[0];
  }

  return fontValue;
}

const fontFamilyMap = {
  "4Ua9rENHsxJlGDuGo1OIlL3L2JB874GPhFI9_IqmuRqGpjeaLi42kO8QvnQOs5beU3yksanMY58mpyzAFyo5T4o":
    "4Ua9rENHsxJlGDuGo1OIlL3L2JB874GPhFI9_IqmuRqGpjeaLi42kO8QvnQOs5beU3yksanMY58mpyzAFyo5T4o 4Ua9rENHsxJlGDuGo1OIlL3L2JB874GPhFI9_IqmuRqGpjeaLi42kO8QvnQOs5beU3yksanMY58mpyzAFyo5T4o",
  "Adelica Brush Demo": "Adelica Brush Demo Demo",
  "Advent Pro Thin": "Advent Pro Thin Regular",
  Agraham: "Agraham Agraham",
  Aileron: "Aileron Regular",
  "Aileron Bold": "Aileron Bold Regular",
  "Akira Expanded": "Akira Expanded Super Bold",
  "Akrobat Light": "Akrobat Light Regular",
  "AkzidenzGroteskBE-LightCn":
    "AkzidenzGroteskBE-LightCn AkzidenzGroteskBE-LightCn",
  "AkzidenzGroteskBE-MdEx": "AkzidenzGroteskBE-MdEx AkzidenzGroteskBE-MdEx",
  "Allrounder Monument Test": "Allrounder Monument Test Regular",
  "Allrounder Monument Test Book": "Allrounder Monument Test Book Regular",
  "Allrounder Monument Test Medium": "Allrounder Monument Test Medium Regular",
  "Alumni Sans Black": "Alumni Sans Black Regular",
  "Arial Rounded MT Bold": "Arial Rounded MT Bold Regular",
  "Austein Script": "Austein Script Regular",
  Author: "Author Bold",
  "Author Medium": "Author Medium Regular",
  "Author Semibold": "Author Semibold Regular",
  "Autumn Flowers": "Autumn Flowers Regular",
  "AutumnFlowers-9YVZK": "AutumnFlowers-9YVZK AutumnFlowers-9YVZK",
  "AutumnFlowers-9YVZK (1)": "AutumnFlowers-9YVZK (1) AutumnFlowers-9YVZK (1)",
  "Avenir Black": "Avenir Black Avenir Black",
  "Avenir LT Pro 65 Medium": "Avenir LT Pro 65 Medium Bold",
  "Avenir LT Std 45 Book": "Avenir LT Std 45 Book Regular",
  "Avenir LT Std 55 Roman": "Avenir LT Std 55 Roman Regular",
  "Avenir LT Std 65 Medium": "Avenir LT Std 65 Medium Bold",
  "Avenir Next Condensed": "Avenir Next Condensed Bold",
  "Avenir Next Condensed Heavy": "Avenir Next Condensed Heavy Regular",
  "AvenirLTStd-Medium": "AvenirLTStd-Medium AvenirLTStd-Medium",
  "AvenirLTStd-Roman": "AvenirLTStd-Roman AvenirLTStd-Roman",
  "AvenirNext LT Pro Bold": "AvenirNext LT Pro Bold Regular",
  Barcelony: "Barcelony Regular",
  "Base Runner JNL": "Base Runner JNL Regular",
  "BBH Hegarty": "BBH Hegarty Regular",
  "Beauty Bright Personal Use": "Beauty Bright Personal Use Regular",
  "Bebas Neue Bold": "Bebas Neue Bold Regular",
  BigParty4Blue: "BigParty4Blue Medium",
  BigSquareDots: "BigSquareDots Regular",
  "Bitter SemiBold": "Bitter SemiBold Regular",
  "BODYCOUNT TYPE PRRSONAL USE Nw": "BODYCOUNT TYPE PRRSONAL USE Nw Regular",
  "Bouncy Scriber": "Bouncy Scriber Bouncy Scriber",
  Branda: "Branda Regular",
  "Brother Signature": "Brother Signature Regular",
  Brotherhood: "Brotherhood Regular",
  "Cassandra Personal Use": "Cassandra Personal Use Regular",
  "Catchy Mager": "Catchy Mager Regular",
  "Choko Extrude": "Choko Extrude Regular",
  "Cinzel Decorative": "Cinzel Decorative Bold",
  "Cinzel Decorative Black": "Cinzel Decorative Black Regular",
  "CitrusGothicSolid-Regular": "CitrusGothicSolid-Regular Regular",
  "Classy Vogue": "Classy Vogue Regular",
  CookieMonster: "CookieMonster Medium",
  "Cormorant SC Light": "Cormorant SC Light Regular",
  "Dancing Script": "Dancing Script Regular",
  "DIAMOND SMITH": "DIAMOND SMITH DIAMOND SMITH",
  "Dienstag Regular": "Dienstag Regular Regular",
  "Edu AU VIC WA NT Pre": "Edu AU VIC WA NT Pre Regular",
  eurostile: "eurostile eurostile",
  FELIXTI: "FELIXTI FELIXTI",
  Figtree: "Figtree Bold",
  "Figtree ExtraBold": "Figtree ExtraBold Regular",
  "Figtree Medium": "Figtree Medium Regular",
  "Filson Pro": "Filson Pro Bold",
  "Filson Pro Black": "Filson Pro Black Regular",
  "Frutiger LT Std 55 Roman": "Frutiger LT Std 55 Roman Regular",
  "FSP DEMO - 800MthrxtnddxtrBld": "FSP DEMO - 800MthrxtnddxtrBld Bold",
  "FSP DEMO - Guidebook": "FSP DEMO - Guidebook Regular",
  "FSP DEMO - Riona Sans": "FSP DEMO - Riona Sans Italic",
  "FSP DEMO - Riona Sans Black": "FSP DEMO - Riona Sans Black Italic",
  "FSP DEMO - Riona Sans Bold": "FSP DEMO - Riona Sans Bold Italic",
  "Futura PT Bold": "Futura PT Bold Regular",
  "Futura Std Medium": "Futura Std Medium Regular",
  "Gill Sans": "Gill Sans Regular",
  "Gill Sans Nova Book": "Gill Sans Nova Book Bold",
  Gilroy: "Gilroy Medium",
  "Gilroy-Bold": "Gilroy-Bold ☞",
  "Gilroy-Heavy": "Gilroy-Heavy ☞",
  "Gilroy-Medium": "Gilroy-Medium ☞",
  "Gilroy-Regular": "Gilroy-Regular ☞",
  "Gilroy-SemiBold": "Gilroy-SemiBold ☞",
  "Golden Beach Personal Use": "Golden Beach Personal Use Bold Italic",
  "Golden Hopes PERSONAL USE ONLY": "Golden Hopes PERSONAL USE ONLY Regular",
  Gotham: "Gotham Bold",
  "Gotham Medium": "Gotham Medium Regular",
  GranainalimpiaRegular: "GranainalimpiaRegular Regular",
  Guazhiru: "Guazhiru Regular",
  "HeadingNow-64Regular": "HeadingNow-64Regular Regular",
  "HELVETICA BLACK OBLIQUE": "HELVETICA BLACK OBLIQUE HELVETICA BLACK OBLIQUE",
  "Helvetica Condensed": "Helvetica Condensed Bold",
  "Helvetica CondensedBlack": "Helvetica CondensedBlack Regular",
  "HELVETICA-BOLD": "HELVETICA-BOLD HELVETICA-BOLD",
  "Houschka Pro DemiBold": "Houschka Pro DemiBold Regular",
  icomoon: "icomoon Regular",
  Inter: "Inter Bold",
  "Inter Black": "Inter Black Regular",
  "Inter ExtraBold": "Inter ExtraBold Regular",
  "Inter Light": "Inter Light Regular",
  "Inter Medium": "Inter Medium Regular",
  "Inter SemiBold": "Inter SemiBold Regular",
  "Inter Thin": "Inter Thin Regular",
  "ITC Avant Garde Gothic": "ITC Avant Garde Gothic Bold",
  "ITC Avant Garde Pro Md": "ITC Avant Garde Pro Md Bold",
  "ITC Avant Garde Std Md": "ITC Avant Garde Std Md Bold",
  Kanit: "Kanit Bold",
  "Kanit Medium": "Kanit Medium Regular",
  "League Gothic": "League Gothic Regular",
  "League Gothic Condensed": "League Gothic Condensed Regular",
  "Lilex Thin": "Lilex Thin Regular",
  "Lobster Two": "Lobster Two Bold",
  Maginia: "Maginia Regular",
  Metropolis: "Metropolis Bold Italic",
  Monik: "Monik Regular",
  Montserrat: "Montserrat Bold",
  "Montserrat ExtraBold": "Montserrat ExtraBold Regular",
  "Montserrat Medium": "Montserrat Medium Regular",
  "Montserrat SemiBold": "Montserrat SemiBold Regular",
  "Montserrat Thin": "Montserrat Thin Regular",
  Morganite: "Morganite Bold",
  "MSung PRC": "MSung PRC Regular",
  Mulish: "Mulish Bold",
  "Mulish Black": "Mulish Black Regular",
  "Mulish ExtraBold": "Mulish ExtraBold Regular",
  NewYork: "NewYork Regular",
  "NEXT ART": "NEXT ART Bold",
  "Nirmala UI": "Nirmala UI Regular",
  Nolan: "Nolan Regular",
  "Noto Sans Black": "Noto Sans Black Regular",
  "Noto Sans Telugu Medium": "Noto Sans Telugu Medium Regular",
  "Object Sans": "Object Sans Regular",
  OPTIFuturaDemiBold: "OPTIFuturaDemiBold Bold",
  "Orpheus Pro": "Orpheus Pro Regular",
  "Oxanium SemiBold": "Oxanium SemiBold Regular",
  Painter: "Painter Regular",
  "Playpen Sans ExtraBold": "Playpen Sans ExtraBold Regular",
  "Playwrite CU": "Playwrite CU Regular",
  Poppins: "Poppins Bold",
  "Poppins ExtraLight": "Poppins ExtraLight Regular",
  "Poppins SemiBold": "Poppins SemiBold Italic",
  "Poppins Thin": "Poppins Thin Italic",
  "PP Object Sans Black": "PP Object Sans Black Italic",
  ResotERose: "ResotERose Regular",
  Roboto: "Roboto Bold",
  "Roboto Black": "Roboto Black Regular",
  RoughSketch: "RoughSketch Regular",
  SakalBharati: "SakalBharati Regular",
  SCHLBKB: "SCHLBKB SCHLBKB",
  Sekuya: "Sekuya Regular",
  "Space Mono": "Space Mono Regular",
  "Super Shake": "Super Shake Regular",
  "Uni Neue-Trial": "Uni Neue-Trial Regular",
  Zuume: "Zuume Bold",
  "Zuume ExtraBold": "Zuume ExtraBold Regular",
  "Zuume SemiBold": "Zuume SemiBold Regular",
};

const getOverlapDiff = (base, object) => {
  if (!base || !object) return {};

  const result = {};

  Object.keys(base).forEach(key => {
    const baseVal = base[key];
    const objVal = object[key];

    // If key not present in object, then ignore
    if (!(key in object)) return;

    // Deep object compare
    if (
      baseVal &&
      objVal &&
      typeof baseVal === "object" &&
      typeof objVal === "object" &&
      !Array.isArray(baseVal) &&
      !Array.isArray(objVal)
    ) {
      const nestedDiff = getOverlapDiff(baseVal, objVal);
      if (Object.keys(nestedDiff).length > 0) {
        result[key] = nestedDiff;
      }
    }
    // Value changed
    else if (baseVal !== objVal) {
      result[key] = objVal;
    }
  });

  return result;
};

module.exports = {
  getDeepDiff,
  getKeys,
  recursiveExtract,
  deepAssign,
  addLayerToLayersObject,
  getLoaderTextObjectData,
  getComputedLayer,
  replaceMediaUrlWithCdn,
  extractCleanFontFamily,
  getInsertAtIndex,
  filterProperties,
  roundOffPropertiesInObject,
  getAllPathsInObject,
  flattenObject,
  sanitizeNumber,
  replaceWithCDN,
  isUrl,
  fontFamilyMap,
  getProxiedUrl,
  getImageNameFromUrl,
  getIsImageRegenerationRequired,
  hasNestedKey,
  extractFileName,
  getOverlapDiff
};
